import React from "react"
import {Shield, AlertTriangle, CheckCircle, HardHat, Eye, HandMetal} from 'lucide-react'
import { motion } from "framer-motion"

const SafetyGuidelines = () => {
  return (
    <div className="bg-zinc-900 min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-zinc-950 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Shield className="text-orange-500 mx-auto mb-6" size={64} />
            <h1 className="text-5xl font-bold text-white mb-6">
              Safety Guidelines for <span className="text-orange-500">Shed Moving Equipment</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Essential safety information for operating shed moving machinery. Learn about proper procedures, protective equipment, and industry best practices.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PPE Requirements */}
      <section className="py-20 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-12 text-center">
            Personal Protective Equipment (PPE)
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-zinc-950 p-8 rounded-lg border border-zinc-800"
            >
              <HardHat className="text-orange-500 mb-4" size={48} />
              <h3 className="text-2xl font-bold text-white mb-4">Head Protection</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span>ANSI-approved hard hats required at all times</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span>Inspect for cracks or damage before each use</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span>Replace every 5 years or after impact</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-zinc-950 p-8 rounded-lg border border-zinc-800"
            >
              <Eye className="text-orange-500 mb-4" size={48} />
              <h3 className="text-2xl font-bold text-white mb-4">Eye Protection</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span>Safety glasses with side shields</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span>Face shields for hydraulic work</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span>Anti-fog coating for clear vision</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-zinc-950 p-8 rounded-lg border border-zinc-800"
            >
              <HandMetal className="text-orange-500 mb-4" size={48} />
              <h3 className="text-2xl font-bold text-white mb-4">Hand Protection</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span>Heavy-duty work gloves for handling</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span>Cut-resistant materials recommended</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span>Good grip in wet or oily conditions</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pre-Operation Checklist */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-4xl font-bold text-white mb-8">
                Pre-Operation Checklist
              </h2>
              <div className="space-y-4">
                <div className="bg-zinc-900 p-6 rounded-lg border border-zinc-800">
                  <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                    <CheckCircle className="text-orange-500" size={24} />
                    Visual Inspection
                  </h3>
                  <ul className="space-y-2 text-gray-300 ml-8">
                    <li>• Check hydraulic hoses for leaks or wear</li>
                    <li>• Inspect wheels and tires for damage</li>
                    <li>• Verify all bolts and connections are tight</li>
                    <li>• Look for structural cracks or damage</li>
                  </ul>
                </div>

                <div className="bg-zinc-900 p-6 rounded-lg border border-zinc-800">
                  <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                    <CheckCircle className="text-orange-500" size={24} />
                    Functional Tests
                  </h3>
                  <ul className="space-y-2 text-gray-300 ml-8">
                    <li>• Test hydraulic lift operation</li>
                    <li>• Check brake functionality</li>
                    <li>• Verify steering response</li>
                    <li>• Test all safety features</li>
                  </ul>
                </div>

                <div className="bg-zinc-900 p-6 rounded-lg border border-zinc-800">
                  <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                    <CheckCircle className="text-orange-500" size={24} />
                    Environment Assessment
                  </h3>
                  <ul className="space-y-2 text-gray-300 ml-8">
                    <li>• Survey terrain and ground conditions</li>
                    <li>• Identify overhead obstacles</li>
                    <li>• Check weather conditions</li>
                    <li>• Plan safe route and clearances</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-4xl font-bold text-white mb-8">
                Critical Safety Warnings
              </h2>
              <div className="space-y-6">
                <div className="bg-red-900/20 border-2 border-red-500 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="text-red-500 flex-shrink-0 mt-1" size={32} />
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Load Capacity</h3>
                      <p className="text-gray-300">
                        Never exceed the manufacturer's specified weight limits. Overloading can cause equipment failure, tipping, or structural damage.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-900/20 border-2 border-red-500 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="text-red-500 flex-shrink-0 mt-1" size={32} />
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Hydraulic Pressure</h3>
                      <p className="text-gray-300">
                        Keep hands and body clear of pinch points during hydraulic operation. Hydraulic fluid under pressure can penetrate skin and cause serious injury.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-900/20 border-2 border-red-500 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="text-red-500 flex-shrink-0 mt-1" size={32} />
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Ground Conditions</h3>
                      <p className="text-gray-300">
                        Avoid soft ground, steep slopes, and unstable surfaces. Equipment can sink or tip on unsuitable terrain causing serious accidents.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Best Practices */}
      <section className="py-20 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-12 text-center">
            Operational Best Practices
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Maintain Clear Communication", description: "Use hand signals and two-way radios when working with a team. Establish clear communication protocols before beginning operations." },
              { title: "Secure the Load Properly", description: "Use appropriate straps, chains, and blocking. Double-check all connections before moving. Never rely on a single attachment point." },
              { title: "Monitor Weather Conditions", description: "Avoid operating in high winds, heavy rain, or icy conditions. Plan operations around favorable weather windows." },
              { title: "Keep Work Area Clear", description: "Maintain at least 20 feet clearance around equipment during operation. Keep bystanders at a safe distance." },
              { title: "Regular Maintenance Schedule", description: "Follow manufacturer's maintenance intervals. Keep detailed service records. Address issues immediately." },
              { title: "Proper Training Required", description: "Ensure all operators receive formal training and certification. Never allow untrained personnel to operate equipment." }
            ].map((practice, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-zinc-950 p-6 rounded-lg border border-zinc-800 hover:border-orange-500 transition-colors"
              >
                <h3 className="text-lg font-bold text-white mb-3">{practice.title}</h3>
                <p className="text-gray-300">{practice.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Procedures */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-12 text-center">
            Emergency Procedures
          </h2>
          <div className="space-y-6">
            <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800">
              <h3 className="text-2xl font-bold text-white mb-4">Equipment Failure</h3>
              <ol className="space-y-3 text-gray-300 list-decimal ml-5">
                <li>Stop operation immediately if any abnormal sounds, movements, or leaks occur</li>
                <li>Move personnel to a safe distance (minimum 50 feet)</li>
                <li>Secure the load using additional supports if possible</li>
                <li>Contact qualified service personnel</li>
                <li>Document the incident for safety records</li>
              </ol>
            </div>

            <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800">
              <h3 className="text-2xl font-bold text-white mb-4">Injury Response</h3>
              <ol className="space-y-3 text-gray-300 list-decimal ml-5">
                <li>Call emergency services (911) immediately for serious injuries</li>
                <li>Secure the equipment and work area to prevent further incidents</li>
                <li>Provide first aid only if trained and it is safe to do so</li>
                <li>Do not move injured persons unless there is immediate danger</li>
                <li>Preserve the scene for investigation</li>
              </ol>
            </div>

            <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800">
              <h3 className="text-2xl font-bold text-white mb-4">Hydraulic Fluid Injection</h3>
              <ol className="space-y-3 text-gray-300 list-decimal ml-5">
                <li>Seek immediate emergency medical attention - this is a medical emergency</li>
                <li>Do not wait for symptoms to appear</li>
                <li>Inform medical personnel that it is a high-pressure injection injury</li>
                <li>Surgery may be required within hours to prevent tissue damage</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-16 bg-zinc-900 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-zinc-950 p-8 rounded-lg border-2 border-orange-500/30">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="text-orange-500" size={24} />
              Important Safety Disclaimer
            </h3>
            <p className="text-gray-300 leading-relaxed">
              This information is for educational purposes only and does not constitute professional safety training or certification. Always follow manufacturer instructions, OSHA regulations, and local safety requirements. Proper training from qualified instructors is required before operating shed moving equipment. MuleShed.com is not responsible for accidents, injuries, or equipment damage resulting from improper use.
            </p>
          </div>
        </div>
      </section>
    </div>
  )
}

export default SafetyGuidelines